/**
 * Companion Watch Apps — portable Workout Engine: TS reference impl (P2a/P2b).
 *
 * Pure, deterministic state machine (no Date.now, no I/O) so the same scripted
 * tick stream always produces the same state + transition + cue sequence — the
 * basis for the cross-language golden-vector conformance suite (Swift/Kotlin
 * mirror). Cardio + mobility (time-phased). Strength is the sibling
 * StrengthEngine (set-based).
 *
 * Behavior characterized from web/src/components/UnifiedWorkoutTimer.tsx:
 *   - progress = (phaseElapsed / durationSeconds) * 100, capped at 100   (:145, :741)
 *   - AUTO-ADVANCE when progress >= 99.9 (float tolerance)               (:743)
 *     → next phase, reset phaseElapsed to 0                              (:760-761)
 *   - only TIME phases auto-advance (the `duration_seconds` guard)        (:737)
 *   - LAST phase completing does NOT auto-end — it signals               (:764-767)
 *     `allPhasesComplete`; the user taps complete (end()).                (:1333)
 *
 * Target/HR-zone eval (P2b-1) + the coaching-cue stream (P2b-3) are net-new
 * watch capability designed to the §3.2 payload + UX-spec voice/haptic taxonomy.
 */

import type {
  EnginePhase,
  EngineState,
  EngineTransition,
  LiveMetrics,
  StructuredWorkout,
  TargetEval,
} from './types';
import type { CueEvent } from './cues';

/** Faithful to UnifiedWorkoutTimer.tsx:743 — the float-tolerant completion gate. */
export const AUTO_ADVANCE_PROGRESS_THRESHOLD = 99.9;

/** Pure phase-progress percentage (0..100, capped) — the SINGLE definition shared
 *  by this engine AND the phone's UnifiedWorkoutTimer (P2c). One formula is why the
 *  watch + phone advance identically; the class's golden vectors test it. Faithful
 *  to the timer's cardio formula `(phaseTime / duration_seconds) * 100`, capped. */
export function phaseProgressPct(durationSeconds: number | undefined, elapsedSec: number): number {
  if (!durationSeconds || durationSeconds <= 0) return 0;
  return Math.min(100, (elapsedSec / durationSeconds) * 100);
}

/** Phases longer than this emit a "last 3s" countdown cue. */
const COUNTDOWN_LEAD_SEC = 3;

export class WorkoutEngine {
  readonly cedarWorkoutId: string;
  readonly activityType: string;
  private readonly phases: EnginePhase[];
  private _state: EngineState;
  private readonly _transitions: EngineTransition[] = [];
  private readonly _cues: CueEvent[] = [];
  private countdownEmittedForPhase = false;
  private prevSide: 'in' | 'over' | 'under' | undefined = undefined;
  private cumulativeDistanceM = 0;
  private phaseStartDistanceM = 0;
  private lastSplitKm = 0;
  /** UNITS-U6: metres per split (1000, or a mile for a miles athlete). */
  private readonly splitDistanceM: number;

  constructor(workout: StructuredWorkout) {
    this.cedarWorkoutId = workout.cedarWorkoutId;
    this.activityType = workout.activityType;
    this.phases = workout.phases ?? [];
    this.splitDistanceM =
      typeof workout.splitDistanceM === 'number' && workout.splitDistanceM > 0 ? workout.splitDistanceM : 1000;
    const empty = this.phases.length === 0;
    this._state = {
      status: empty ? 'complete' : 'running',
      currentPhaseIndex: 0,
      phaseElapsedSec: 0,
      totalElapsedSec: 0,
      phaseProgressPct: 0,
      isLastPhase: this.phases.length <= 1,
      allPhasesComplete: empty,
      rep: this.phases[0]?.rep,
    };
    this._state.target = WorkoutEngine.evalTarget(this.phases[0], undefined);
    if (!empty) this.emitStartCue(this.phases[0], 0);
  }

  /** Defensive copies — callers cannot mutate engine internals. */
  get state(): EngineState {
    return { ...this._state };
  }

  get transitions(): EngineTransition[] {
    return this._transitions.map((t) => ({ ...t }));
  }

  get cues(): CueEvent[] {
    return this._cues.map((c) => ({ ...c }));
  }

  private currentPhase(): EnginePhase | undefined {
    return this.phases[this._state.currentPhaseIndex];
  }

  private static progress(phase: EnginePhase | undefined, elapsedSec: number): number {
    return phaseProgressPct(phase?.durationSeconds, elapsedSec);
  }

  /** Evaluate the current phase's target band against the live sample. Pure:
   *  undefined when the phase has no target; current/inZone undefined when the
   *  relevant live metric is absent. */
  private static evalTarget(
    phase: EnginePhase | undefined,
    live: LiveMetrics | undefined,
  ): TargetEval | undefined {
    const t = phase?.target;
    if (!t) return undefined;
    let current: number | undefined;
    if (live) {
      // EXPLICIT per metric. The previous ternary ended in `: live.power`, so any
      // metric added to TargetMetric would silently have read the POWER sample.
      // `rpe` has no sensor at all -> current stays undefined -> no inZone, no
      // side, no drift cues. Display-only by construction.
      current =
        t.metric === 'hr'
          ? live.hr
          : t.metric === 'pace'
            ? live.paceSecPerKm
            : t.metric === 'power'
              ? live.power
              : undefined;
    }
    const inZone = current === undefined ? undefined : current >= t.low && current <= t.high;
    let side: 'in' | 'over' | 'under' | undefined;
    if (current !== undefined) {
      if (inZone) side = 'in';
      // Effort-relative + metric-aware: for pace (sec/km, lower = faster),
      // BELOW the band (current < low) is faster = OVER-effort; for hr/power,
      // ABOVE the band high is OVER-effort.
      else if (t.metric === 'pace') side = current < t.low ? 'over' : 'under';
      else side = current > t.high ? 'over' : 'under';
    }
    return { metric: t.metric, low: t.low, high: t.high, current, inZone, side };
  }

  /** Phase-start cue (recovery phases get a distinct kind), double-tap haptic. */
  private emitStartCue(phase: EnginePhase | undefined, atSec: number): void {
    if (!phase) return;
    const kind = phase.phaseType === 'recovery' ? 'recovery_start' : 'phase_start';
    this._cues.push({ kind, atSec, haptic: 'double_tap', data: { phaseType: phase.phaseType } });
  }

  private advanceTo(nextIndex: number, reason: 'auto' | 'manual'): void {
    const s = this._state;
    const from = s.currentPhaseIndex;
    s.currentPhaseIndex = nextIndex;
    s.phaseElapsedSec = 0;
    s.phaseProgressPct = 0;
    s.isLastPhase = nextIndex >= this.phases.length - 1;
    this.countdownEmittedForPhase = false;
    this.prevSide = undefined; // re-alert target drift fresh per phase
    this.phaseStartDistanceM = this.cumulativeDistanceM; // distance goal is per-phase
    s.rep = this.phases[nextIndex]?.rep;
    this._transitions.push({
      type: 'phaseAdvance',
      atSec: s.totalElapsedSec,
      fromPhaseIndex: from,
      toPhaseIndex: nextIndex,
      reason,
    });
    this.emitStartCue(this.phases[nextIndex], s.totalElapsedSec);
  }

  private markAllComplete(reason: 'auto' | 'manual'): void {
    const s = this._state;
    if (s.allPhasesComplete) return;
    s.allPhasesComplete = true;
    this._transitions.push({
      type: 'allPhasesComplete',
      atSec: s.totalElapsedSec,
      fromPhaseIndex: s.currentPhaseIndex,
      reason,
    });
    this._cues.push({ kind: 'workout_complete', atSec: s.totalElapsedSec, haptic: 'triple_tap' });
  }

  /** Advance time by `deltaSec` (default 1). No-op unless running. */
  tick(deltaSec = 1, live?: LiveMetrics): EngineState {
    const s = this._state;
    if (s.status !== 'running') return this.state;

    s.totalElapsedSec += deltaSec;
    s.phaseElapsedSec += deltaSec;

    // Cumulative distance is session-absolute; the watch session feeds it (EX-2).
    if (live?.distanceM !== undefined) this.cumulativeDistanceM = live.distanceM;

    const phase = this.currentPhase();
    s.phaseProgressPct = WorkoutEngine.progress(phase, s.phaseElapsedSec);

    // Goal-aware termination (EX-2): distance / hr_recovery / time(legacy) / open.
    let shouldAdvance = false;
    let recovered = false;
    const goal = phase?.goal;
    if (goal?.kind === 'distance' && goal.distanceMeters && goal.distanceMeters > 0) {
      shouldAdvance = this.cumulativeDistanceM - this.phaseStartDistanceM >= goal.distanceMeters;
    } else if (goal?.kind === 'hr_recovery') {
      const hr = live?.hr;
      if (hr !== undefined && goal.recoveryHrTarget !== undefined && hr <= goal.recoveryHrTarget) {
        shouldAdvance = true;
        recovered = true;
      } else if (goal.durationSeconds && s.phaseElapsedSec >= goal.durationSeconds) {
        shouldAdvance = true; // time-cap fallback (no recovered cue)
      }
    } else {
      // time (legacy durationSeconds) / open. Only TIME phases auto-advance.
      const isTimePhase = !!phase && !!phase.durationSeconds && phase.durationSeconds > 0;
      shouldAdvance = isTimePhase && s.phaseProgressPct >= AUTO_ADVANCE_PROGRESS_THRESHOLD;
    }

    if (shouldAdvance) {
      if (recovered) {
        this._cues.push({ kind: 'recovered', atSec: s.totalElapsedSec, haptic: 'strong_buzz' });
      }
      if (s.currentPhaseIndex < this.phases.length - 1) {
        this.advanceTo(s.currentPhaseIndex + 1, 'auto');
      } else {
        this.markAllComplete('auto');
      }
    }

    s.target = WorkoutEngine.evalTarget(this.currentPhase(), live);

    // Countdown cue: last COUNTDOWN_LEAD_SEC of a timed phase (>lead), once/phase.
    const phaseNow = this.currentPhase();
    if (
      phaseNow &&
      phaseNow.durationSeconds &&
      phaseNow.durationSeconds > COUNTDOWN_LEAD_SEC &&
      !this.countdownEmittedForPhase &&
      phaseNow.durationSeconds - s.phaseElapsedSec <= COUNTDOWN_LEAD_SEC
    ) {
      this.countdownEmittedForPhase = true;
      this._cues.push({ kind: 'countdown', atSec: s.totalElapsedSec, haptic: 'rising_buzz' });
    }

    // Target-drift cue: the live PRIMARY metric crossed OUT of the band, either
    // side, edge-triggered (fires once per transition into a new out-state).
    // Two-sided + metric-aware (pace inversion handled in evalTarget). EX-1.
    const tgt = s.target;
    const side = tgt?.side;
    if (tgt && (side === 'over' || side === 'under')) {
      if (side !== this.prevSide) {
        this._cues.push({
          kind: 'target_drift',
          atSec: s.totalElapsedSec,
          haptic: 'double_buzz',
          data: { side, metric: tgt.metric, current: tgt.current },
        });
      }
      this.prevSide = side;
    } else if (side === 'in') {
      this.prevSide = 'in';
    }

    // Per-km split cue (auto-lap), independent of phase goals. EX-2.
    // UNITS-U6: every `splitDistanceM` metres; the cue says which unit it
    // counts — `{ km }` for the per-km split (unchanged), `{ mi }` otherwise.
    const n = Math.floor(this.cumulativeDistanceM / this.splitDistanceM);
    if (n > this.lastSplitKm) {
      this.lastSplitKm = n;
      this._cues.push({
        kind: 'split', atSec: s.totalElapsedSec, haptic: 'double_tap',
        data: this.splitDistanceM === 1000 ? { km: n } : { mi: n },
      });
    }

    s.distanceM = this.cumulativeDistanceM;
    return this.state;
  }

  pause(): EngineState {
    if (this._state.status === 'running') this._state.status = 'paused';
    return this.state;
  }

  resume(): EngineState {
    if (this._state.status === 'paused') this._state.status = 'running';
    return this.state;
  }

  /** Manual advance to the next phase. On the last phase, signals complete. */
  skipPhase(): EngineState {
    const s = this._state;
    if (s.status === 'complete') return this.state;
    if (s.currentPhaseIndex < this.phases.length - 1) {
      this.advanceTo(s.currentPhaseIndex + 1, 'manual');
    } else {
      this.markAllComplete('manual');
    }
    return this.state;
  }

  /** Explicit completion (the user taps "complete"). */
  end(): EngineState {
    if (this._state.status !== 'complete') {
      this._state.status = 'complete';
      this._transitions.push({ type: 'workoutEnded', atSec: this._state.totalElapsedSec });
    }
    return this.state;
  }
}
